import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { NewsItem, NewsResponse } from '../types';

export const News: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);

  useEffect(() => {
    fetchNews();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchNews, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response: NewsResponse = await apiService.getNewsHeadlines(100);
      
      // Sort by created_at timestamp (newest first)
      const sortedNews = response.data.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      
      setNews(sortedNews);
      setLastRefreshTime(new Date());
    } catch (err) {
      console.error('Error loading news:', err);
      setError('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getSentimentColor = (sentiment: string): string => {
    switch (sentiment.toLowerCase()) {
      case 'positive':
        return '#10b981'; // green
      case 'negative':
        return '#ef4444'; // red
      case 'neutral':
      default:
        return '#6b7280'; // gray
    }
  };

  if (loading) {
    return (
      <div className="component-container">
        <div className="component-header">
          <h2 className="component-title">Market News</h2>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span className="loading-text">Loading news...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="component-container">
        <div className="component-header">
          <h2 className="component-title">Market News</h2>
        </div>
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button 
            onClick={fetchNews}
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="component-container">
      <div className="component-header">
        <h2 className="component-title">Market News</h2>
        <div className="refresh-info">
          {lastRefreshTime && (
            <span className="last-refresh">
              Last updated: {lastRefreshTime.toLocaleTimeString()}
            </span>
          )}
          <button 
            onClick={fetchNews}
            className="refresh-button"
            title="Refresh news"
          >
            🔄
          </button>
        </div>
      </div>

      <div className="news-grid">
        {news.length === 0 ? (
          <div className="no-news">
            <span>No news available</span>
          </div>
        ) : (
          news.map((item, index) => (
            <div key={`${item.created_at}-${index}`} className="news-card">
              <div className="news-card-header">
                <div className="news-card-source">
                  {item.source}
                  {item.is_major && <span className="major-badge">MAJOR</span>}
                </div>
                <div className="news-card-time">
                  {formatTime(item.created_at)}
                </div>
              </div>
              
              <div className="news-card-headline">
                {item.headline}
              </div>
              
              <div className="news-card-footer">
                <div 
                  className="news-card-sentiment"
                  style={{ color: getSentimentColor(item.sentiment) }}
                >
                  {item.sentiment.toUpperCase()}
                </div>
                {item.tickers.length > 0 && (
                  <div className="news-card-tickers">
                    {item.tickers.map(ticker => (
                      <span key={ticker} className="ticker-tag">
                        {ticker}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
